// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import 'moment/locale/fr';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import 'pdfjs-dist/build/pdf.worker';
import 'pdfjs-dist/build/pdf.worker.entry';

import AppView from '../layouts/AppView';

import TextInput from '../shared/TextInput';
import DropdownInput from '../shared/DropdownInput';
import Button from '../shared/Button';
import Text from '../shared/Typography';
import SignaturePad from '../shared/SignaturePad';
import Card from '../shared/Card';
import PDFSection from '../shared/PDFSection';
import VersionedText from '../shared/VersionedText';
import {
  validateHCN,
  formatHCN,
  validatePostalCode,
  getValidPostalCode,
  isValidDOB
} from '../../validators/authorize-request';

import illoSuccess from '../../assets/illustrations/share-success.png';

import authorizeRequestService from '../../services/authorize-request-service';
import MapInput, { PlacesResultObject } from '../shared/MapInput';
import { getRouteTFunction } from '../../i18n';
import { isNotUndefined } from '../../utils/utils';
import DateInputGroup from '../shared/DateInputGroup';
import Icon from '../shared/Icon';

type Props = {
  match: {
    params: {
      token: string
    },
  },
  history: Object,
  fetchError?: string,
  loading: boolean,
  expiryDate: string,
  location?: {
    search?: string,
  }
} & WithTranslation;

type State = {
  error: string,
  validationErrors: Object,
  verifyFailed: boolean,
  hasVerifiedPatient: boolean,
  formType: 'full' | 'verification',
  hasSigned: boolean,
  hcn: string,
  province: string,
  dob: string,
  user: Object,
  loading: boolean,
  invalid: boolean,
  accessToken: string,
  firstName: string,
  lastName: string,
  addressLine1: string,
  addressLine2: string,
  postalCode: string,
  city: string,
  phone: string
};

const FORM_TYPES = {
  FULL: 'full',
  VERIFICATION_ONLY: 'verification'
};

const fullFormAllowedNames = [
  'firstName',
  'lastName',
  'addressLine1',
  'addressLine2',
  'city',
  'postalCode',
  'province',
  'hcn',
  'dob'
];
const verificationFormAllowedNames = ['dob', 'hcn'];

type ConditionalInputProps = {
  children: React.ReactChildren,
  name: string,
  formType: 'full' | 'verification'
};

const ConditionalInput = ({
  children,
  name,
  formType
}: ConditionalInputProps) => {
  if (formType === FORM_TYPES.FULL) {
    if (fullFormAllowedNames.indexOf(name) >= 0) {
      return <React.Fragment>{children}</React.Fragment>;
    }
  } else if (formType === FORM_TYPES.VERIFICATION_ONLY) {
    if (verificationFormAllowedNames.indexOf(name) >= 0) {
      return <React.Fragment>{children}</React.Fragment>;
    }
  }
  return null;
};

type AppNavLogoProps = {
  institutionName: string,
  logoURL?: string
};

const AppNavInstitutionLogo = ({
  institutionName,
  logoURL
}: AppNavLogoProps) => {
  return (
    <div className='partner-logo'>
      {/* eslint-disable-next-line i18next/no-literal-string */}
      {logoURL && (
        <img
          src={logoURL}
          // eslint-disable-next-line i18next/no-literal-string
          alt='Institution Logo'
          className='partner-logo-image'
        />
      )}
      <span className='partner-logo-type'>{institutionName}</span>
    </div>
  );
};

const isNotEmpty = (value: any) => {
  return value !== null && value !== undefined;
};

const getString = (value: string) => {
  if (isNotEmpty(value)) {
    return value;
  }
  return '';
};

class AuthorizeRequest extends Component<Props, State> {
  state = {
    error: '',
    validationErrors: {
      hcn: null,
      dob: null,
      postalCode: null
    },
    loading: false,
    verifyFailed: false,
    hasVerifiedPatient: false,
    hasSigned: false,
    hcn: '',
    province: '',
    dob: '',
    user: {},
    invalid: false,
    accessToken: '',
    validTokens: [],
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    phone: '',
    formType: null,
    requestingInstitution: { id: null, institution_name: '', logoURL: '' },
    confirmationText: null,
    hasConfirmed: false,
    pages: [],
    validateOnChange: false,
    disabled: false,
    timer: null,
    timeLeft: null
  };

  state: State;
  props: Props;

  addValidationErrors = (errorMessages) => {
    this.setState({
      validationErrors: { ...this.state.validationErrors, ...errorMessages }
    });
  };

  validateValues = (
    fieldNames: string[] | string,
    fieldValue: string,
    province: string
  ) => {
    const { t } = this.props;
    const validation = t('validation', { returnObjects: true });
    const rules = {
      firstName: (value: string) => {
        if (!value) {
          return validation.fname_required;
        } else if (value.length < 2) {
          return validation.fname_two_char;
        }
        return null;
      },
      lastName: (value: string) => {
        if (!value) {
          return validation.lname_required;
        } else if (value.length < 2) {
          return validation.lname_two_char;
        }
        return null;
      },
      postalCode: (value: string) => {
        if (!value) {
          return validation.postal_code;
        }
        return validatePostalCode(value, validation.postal_code_invalid);
      },
      hcn: (value: string, provinceName: string) => {
        if (!value) return validation.health_card;
        const errorMessage = validateHCN(
          value,
          provinceName,
          validation.health_card_invalid,
          validation.example
        );
        return errorMessage;
      },
      dob: (value: string) => {
        if (!value || !isValidDOB(value)) return validation.invalid_dob;
        return null;
      }
    };
    const errorMessages = {};
    if (fieldNames && fieldNames.length > 1) {
      fieldNames.map((name) => {
        const applyRule = rules[name];
        const value = this.state[name];
        if (applyRule) errorMessages[name] = applyRule(value, province);
        return null;
      });
    } else if (fieldNames && fieldNames.length === 1) {
      const applyRule = rules[fieldNames[0]];
      const value = isNotUndefined(fieldValue)
        ? fieldValue
        : this.state[fieldNames[0]];
      if (applyRule) errorMessages[fieldNames[0]] = applyRule(value, province);
    }
    this.addValidationErrors(errorMessages);
  };

  formatValue = (fieldName: string, fieldValue: string, province: string) => {
    if (fieldName === 'hcn') {
      return formatHCN(fieldValue, province);
    }
    if (fieldName === 'postalCode') {
      return getValidPostalCode(fieldValue);
    }
    return fieldValue;
  };

  componentDidMount() {
    // Before allowing to attempt access, check if request is still awaiting patient authorization
    const { token } = this.props.match.params;
    authorizeRequestService
      .checkValidity(token) // might be multiple tokens; backend will handle case
      .then((response) => {
        this.setState({
          validTokens: response.requestTokens,
          province: response.province,
          requestingInstitution: response.requestingInstitution
        });
        if (response.accessToken) {
          authorizeRequestService
            .downloadInstitutionLogo(response.accessToken)
            .then((data) => {
              if (data) {
                const fileBlob = new Blob([data], {
                  type: 'image/jpeg, image/png'
                });

                this.setState((prev) => ({
                  requestingInstitution: {
                    ...prev.requestingInstitution,
                    logoURL: URL.createObjectURL(fileBlob)
                  }
                }));
              }
            });

          const verifyAuthDisabled = localStorage.getItem('verifyAuthDisabled');
          const verifyAuthTimeoutExpire = localStorage.getItem('verifyAuthTimeoutExpire');
          const verifyAuthError = localStorage.getItem('verifyAuthError');

          if (verifyAuthDisabled === 'true') {
            this.setState({ disabled: true, error: JSON.parse(verifyAuthError) });
            this.startTimer(verifyAuthTimeoutExpire);
          }
        }
        if (response.formType) {
          this.setState({ formType: response.formType });
        } else {
          this.setState({ invalid: true });
        }
      })
      .catch((err) => {
        console.log(err);
        this.setState({ invalid: true });
      });
  }

  onProvinceChange = (e: SyntheticInputEvent): void => {
    this.setState({ province: e.target.value });
    // re-validate HCN, if it exists, according to provincial specifications
    if (this.state.hcn) {
      const formattedVal = formatHCN(this.state.hcn, e.target.value);
      const localError = validateHCN(
        this.state.hcn,
        e.target.value,
        this.props.t('validation.health_card_invalid'),
        this.props.t('validation.example')
      );
      this.setState({
        hcn: formattedVal,
        validationErrors: { ...this.state.validationErrors, hcn: localError }
      });
    }
  };

  handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    { touched }?: { touched: boolean } = {}
  ) => {
    const formattedValue = this.formatValue(
      e.target.name,
      e.target.value,
      this.state.province
    );
    this.setState({ [e.target.name]: formattedValue });
    if (touched || this.state.validateOnChange) {
      this.validateValues([e.target.name], formattedValue, this.state.province);
    }
    if (this.state.error) this.setState({ error: '' });
  };

  onDateChange = (value: string, { touched }?: { touched: boolean } = {}) => {
    this.setState({ dob: value });
    if (touched || this.state.validateOnChange) {
      this.validateValues(['dob'], value, this.state.province);
    }
    if (this.state.error) this.setState({ error: '' });
  };

  startTimer = (end) => {
    const endDate = new Date(end);
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = endDate - now;
      if (distance < 0) {
        clearInterval(timer);
        this.setState({ disabled: false, clicks: 0, timeLeft: null });
        localStorage.removeItem('verifyAuthDisabled');
        localStorage.removeItem('verifyAuthTimeoutExpire');
        localStorage.removeItem('verifyAuthError');
      } else {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        let timeLeft = '';
        if (days > 0) {
          timeLeft += `${days}d `;
        }
        if (hours > 0 || days > 0) {
          timeLeft += `${hours}h `;
        }
        timeLeft += `${minutes}m ${seconds}s`;
        this.setState({ timeLeft });
      }
    }, 1000);
    this.setState({ timer });
  };

  componentWillUnmount() {
    clearInterval(this.state.timer);
  }

  verifyPatient = () => {
    this.setState({ loading: true, error: null });
    if (!this.isFormReady()) {
      this.setState({ loading: false });
      return;
    }
    if (this.state.formType === FORM_TYPES.VERIFICATION_ONLY) {
      authorizeRequestService
        .verifyUser(this.state.validTokens, {
          dob: this.state.dob,
          hcn: this.state.hcn,
          province: this.state.province
        })
        .then((response) => {
          this.setState({
            hasVerifiedPatient: true,
            loading: false,
            user: response.user,
            accessToken: response.accessToken,
            confirmationText: VersionedText(response.confirmation_text, {
              institution_name:
                this.state.requestingInstitution.institution_name
            })
          });
          this.state.validTokens.forEach((requestToken) => {
            authorizeRequestService
              .downloadConsentForm(this.state.accessToken, requestToken)
              .then((data) => {
                const file = new Blob([data], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);

                pdfjsLib
                  .getDocument(fileURL)
                  .promise.then((pdf) => {
                    if (pdf && pdf.numPages > 0) {
                      return Promise.all(
                        Array.from({ length: pdf.numPages }).map((v, i) => {
                          return pdf.getPage(i + 1);
                        })
                      );
                    }
                    return Promise.resolve();
                  })
                  .then((pages) => {
                    if (pages) {
                      this.setState((prev) => ({
                        pages: [...prev.pages, ...pages]
                      }));
                    }
                  });
              });
          });
        })
        .catch((err) => {
          if (err && err.message && err.message.auth_attempt_count >= 3) {
            localStorage.setItem('token', this.props.match.params);
            localStorage.setItem('verifyAuthDisabled', 'true');
            localStorage.setItem('verifyAuthTimeoutExpire', err.message.timeout_expire);
            localStorage.setItem('verifyAuthError', JSON.stringify(err));
            this.setState({ disabled: true });
            this.startTimer(err.message.timeout_expire);
          }
          console.log(err);
          this.setState({ error: err, loading: false });
        });
    } else if (this.state.formType === FORM_TYPES.FULL) {
      const {
        firstName,
        lastName,
        addressLine1,
        addressLine2,
        city,
        postalCode,
        province,
        hcn,
        dob,
        phone
      } = this.state;
      const user = {
        first_name: firstName,
        last_name: lastName,
        city,
        dob,
        insurance_number: hcn,
        street_address: addressLine2
          ? `${addressLine2}-${addressLine1}`
          : addressLine1,
        province,
        postal_code: postalCode,
        phone
      };
      authorizeRequestService
        .updateRequestPatient(this.state.validTokens, user)
        .then((response) => {
          this.setState({
            hasVerifiedPatient: true,
            loading: false,
            user: response.user,
            accessToken: response.accessToken
          });
        })
        .catch((err) => {
          console.log(err);
          this.setState({ error: err, loading: false });
        });
    }
  };

  createSignature = (image) => {
    const { t } = this.props;
    this.setState({
      loading: true,
      error: null
    });
    authorizeRequestService
      .create(image, this.state.accessToken)
      .then(() => {
        this.setState({ hasSigned: true, loading: false });
      })
      .catch((err) => {
        console.log(err);
        const errMsg = t('signature_err');
        this.setState({ error: errMsg, loading: false });
      });
  };

  mapPlacesResultObjectToState = (e: PlacesResultObject) => {
    const addressLine1 = [e.street_number, e.route].join(' ').trim();
    const province = getString(e.administrative_area_level_1);
    const city = getString(e.locality);
    const postalCode = getString(e.postal_code);
    this.setState({ addressLine1, province, city, postalCode });
    this.validateValues(['postalCode'], postalCode);
    if (this.state.hcn) {
      const formattedVal = formatHCN(this.state.hcn, province);
      const localError = validateHCN(
        this.state.hcn,
        province,
        this.props.t('validation.health_card_invalid'),
        this.props.t('validation.example')
      );
      this.setState({
        hcn: formattedVal,
        validationErrors: { ...this.state.validationErrors, hcn: localError }
      });
    }
  };

  isFormReady = () => {
    this.setState({ validateOnChange: true });
    const fullFormDataKeys = [
      'firstName',
      'lastName',
      'addressLine1',
      'city',
      'postalCode',
      'province',
      'hcn',
      'dob'
    ];
    const verificationFormDataKeys = ['province', 'hcn', 'dob'];
    const currentFormDataKeys = () => {
      if (this.state.formType === FORM_TYPES.FULL) return fullFormDataKeys;
      if (this.state.formType === FORM_TYPES.VERIFICATION_ONLY) {
        return verificationFormDataKeys;
      }
      return null;
    };
    if (!currentFormDataKeys()) return false;
    // this.validateValues('hcn', this.state['hcn'], this.state.province);
    this.validateValues(currentFormDataKeys(), [], this.state.province);
    return !currentFormDataKeys().some(
      (key) => !this.state[key] || this.state.validationErrors[key]
    );
  };

  render() {
    const {
      hasVerifiedPatient,
      hasSigned,
      loading,
      hcn,
      province,
      error,
      invalid,
      validationErrors,
      firstName,
      lastName,
      addressLine1,
      addressLine2,
      city,
      postalCode,
      pages,
      disabled,
      timeLeft
    } = this.state;
    const { t } = this.props;
    const routesTFunction = getRouteTFunction();

    return (
      <AppView
        viewType='private-body'
        partnerLogo={AppNavInstitutionLogo({
          institutionName: this.state.requestingInstitution.institution_name,
          logoURL: this.state.requestingInstitution.logoURL
        })}
      >
        <div className='content-container'>
          <main>
            {invalid && (
              <div className='authorize-request__container is-center'>
                <Text h1 align='center'>
                  {t('not_right')}
                </Text>
                <Text paragraph className='is-subtitle' align='center'>
                  {t('record_req_not_found')}
                </Text>
                <Button
                  className='is-primary'
                  label={t('open_dot')}
                  onClick={() => {
                    this.props.history.push(
                      routesTFunction('redirectPaths./apps')
                    );
                  }}
                />
              </div>
            )}
            {!invalid && !hasVerifiedPatient && (
              <div className='authorize-request__container is-center'>
                <Text h2 color='ship-cove' className='authorize-request__title'>
                  {t('auth_request')}
                </Text>
                <Text paragraph color='ship-cove'>
                  {t('verify_hcn_dob')}
                </Text>
                {error &&
                  error.message &&
                  error.message.auth_attempt_count >= 3 && (
                    <Card className='authorize-request__error-container'>
                      <Icon
                        className='authorize-request__error-container-icon'
                        name='error'
                        size='medium'
                      />
                      <div>
                        <Text
                          paragraph
                          className='authorize-request__error-container-text'
                        >
                          {error &&
                          (error.code === 'US_MATCHING_DOB' ||
                            error.code === 'US_MATCHING_HCN' ||
                            error.code === 'US_MATCHING_HCN_DOB' ||
                             error.code === 'US_TIMEOUT')
                            ? t('informationMismatch', {
                                institution:
                                  this.state.requestingInstitution
                                    .institution_name
                              })
                            : error.message.message}
                        </Text>
                      </div>
                    </Card>
                  )}
                <Card className='authorize-request__card-container mt-60'>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'firstName'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('fname')}
                    </Text>
                    <TextInput
                      id='firstName'
                      name='firstName'
                      value={firstName}
                      onChange={this.handleChange}
                      error={validationErrors.firstName}
                      className='narrowed'
                    />
                  </ConditionalInput>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'lastName'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('lname')}
                    </Text>
                    <TextInput
                      id='lastName'
                      name='lastName'
                      value={lastName}
                      onChange={this.handleChange}
                      error={validationErrors.lastName}
                      className='narrowed'
                    />
                  </ConditionalInput>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'addressLine1'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('address')}
                    </Text>
                    <MapInput
                      onChange={this.handleChange}
                      value={addressLine1}
                      setValues={(e: PlacesResultObject) => {
                        this.mapPlacesResultObjectToState(e);
                      }}
                      className='narrowed'
                    />
                  </ConditionalInput>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'addressLine2'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('addr_unit_number')}
                    </Text>
                    <TextInput
                      id='addressLine2'
                      name='addressLine2'
                      value={addressLine2}
                      onChange={this.handleChange}
                      className='narrowed'
                    />
                  </ConditionalInput>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'city'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('city')}
                    </Text>
                    <TextInput
                      id='city'
                      name='city'
                      value={city}
                      onChange={this.handleChange}
                      className='narrowed'
                    />
                  </ConditionalInput>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'postalCode'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('postal_code')}
                    </Text>
                    <TextInput
                      id='postalCode'
                      name={'postalCode'}
                      value={postalCode}
                      onChange={this.handleChange}
                      error={validationErrors.postalCode}
                      className='narrowed'
                    />
                  </ConditionalInput>
                  <ConditionalInput
                    formType={this.state.formType}
                    name={'province'}
                  >
                    <Text paragraph color='grey-40'>
                      {t('province')}
                    </Text>
                    <DropdownInput
                      name='province'
                      onChange={this.onProvinceChange}
                      className='narrowed'
                      value={province}
                      selected={province}
                      province
                      required
                    />
                  </ConditionalInput>
                  <ConditionalInput formType={this.state.formType} name={'dob'}>
                    <DateInputGroup
                      disabled={disabled || loading}
                      onChange={this.onDateChange}
                      t={t}
                      error={
                        error &&
                        (error.code === 'US_MATCHING_DOB' ||
                          error.code === 'US_MATCHING_HCN_DOB')
                          ? t('dobMismatch', {
                              institution:
                                this.state.requestingInstitution
                                  .institution_name
                            })
                          : validationErrors.dob
                      }
                    />
                  </ConditionalInput>
                  <ConditionalInput formType={this.state.formType} name={'hcn'}>
                    <Text paragraph color='grey-40' className='mt-10'>
                      {t('hcn')}
                    </Text>
                    <TextInput
                      disabled={disabled || loading}
                      id='hcn'
                      name='hcn'
                      value={hcn}
                      onChange={this.handleChange}
                      error={
                        error &&
                        (error.code === 'US_MATCHING_HCN' ||
                          error.code === 'US_MATCHING_HCN_DOB')
                          ? t('hcnMismatch', {
                              institution:
                                this.state.requestingInstitution
                                  .institution_name
                            })
                          : validationErrors.hcn
                      }
                      required
                      className='narrowed'
                    />
                  </ConditionalInput>

                  <div className='authorize-request__container is-center'>
                    <Button
                      id='submit'
                      className='is-primary no-transform'
                      label={
                        disabled
                          ? t('tryAgainResend', { resendSeconds: timeLeft || '0m 00s' })
                          : t('verify')
                      }
                      isLoading={loading}
                      isDisabled={disabled || loading}
                      onClick={this.verifyPatient}
                      style={{ textTransform: 'none' }}
                    />
                  </div>
                </Card>
              </div>
            )}
            {hasVerifiedPatient && !hasSigned && (
              <div className='is-center'>
                <div className='authorize-request__signature_header_container is-center'>
                  <Text
                    h2
                    color='ship-cove'
                    className='authorize-request__title'
                  >
                    {t('auth_request')}
                  </Text>
                  <Text paragraph color='ship-cove'>
                    {t('auth_draw_esign', {
                      institution_name:
                        this.state.requestingInstitution.institution_name
                    })}
                  </Text>
                </div>
                <PDFSection pages={pages} />
                <SignaturePad
                  onSubmit={this.createSignature}
                  loading={loading}
                  confirmationText={this.state.confirmationText}
                  submitLabel={t('authorize_request')}
                  hasConsent={this.state.hasConfirmed}
                  onConfirmationChange={() =>
                    this.setState((prev) => ({
                      hasConfirmed: !prev.hasConfirmed
                    }))
                  }
                />
                {error && (
                  <Text
                    paragraph
                    className='authorize-request__container--error'
                  >
                    {error}
                  </Text>
                )}
              </div>
            )}
            {hasSigned && (
              <div className='authorize-request is-center'>
                <img
                  className='authorize-request__illo'
                  src={illoSuccess}
                  alt={t('success_card_alt')}
                />
                <Text h1 className='authorize-request__title' align='center'>
                  {t('req_authorized')}
                </Text>
                <Text paragraph align='center'>
                  {t('thanks')}
                </Text>
              </div>
            )}
          </main>
        </div>
      </AppView>
    );
  }
}

const mapStateToProps = (appState) => ({
  loading: appState.shared && appState.shared.loading
});

const mapDispatchToProps = (_dispatch) => ({});

export default withTranslation('authorizeRequest')(
  connect(mapStateToProps, mapDispatchToProps)(AuthorizeRequest)
);



// WEBPACK FOOTER //
// ./src/components/pages/authorize-request.js